import { useMemo } from "react";
import { useAuth } from "contexts/AuthContext";
import { useCause } from "contexts/CauseContext";
import { useTeam } from "contexts/TeamContext";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import getCauseTypesByIds from "utils/causes/getCauseTypesByIds";

const useCauseProcessor = () => {
  const { causes } = useCause();
  const { userId } = useAuth();
  const {
    causeTypes,
    activeCauseTypes,
    defaultCauseTypes,
    causeTypesTranslations,
  } = useCause();
  const { teamCauseTypes, activeTeamCauseTypes, allTeamCauseTypeTranslations } =
    useTeam();

  // Helper: robustly build a tri-lingual object from a translation doc + inline fallback
  const toLangTriplet = (tDoc, inline, fallback) => {
    // any
    // {"ru":"","en":"Upcoming deadline","et":""}
    // string
    console.log("debug toLangTriplet " + tDoc);
    console.log("debug toLangTriplet " + JSON.stringify(inline));
    console.log("debug toLangTriplet " + fallback);
    // If inline already has the proper structure (like team causes), use it directly
    if (
      inline &&
      typeof inline === "object" &&
      (inline.ru || inline.en || inline.et)
    ) {
      return {
        ru: inline.ru || "",
        en: inline.en || "",
        et: inline.et || "",
      };
    }

    // Some docs have explicit ru/en/et, some only {lang, translation}, some use translatedValue
    const pick = (lang) =>
      (tDoc && tDoc[lang]) ??
      (tDoc && tDoc.lang === lang ? tDoc.translation : undefined) ??
      (tDoc && tDoc.translatedValue) ??
      (inline && inline[lang]) ??
      fallback ??
      null;
    return { ru: pick("ru"), en: pick("en"), et: pick("et") };
  };

  // Ensure we have arrays (defensive: UserContext may return object in edge cases)
  const toArray = (v) => (Array.isArray(v) ? v : []);

  // Merge and deduplicate cause types
  const mergedCauseTypes = useMemo(() => {
    const merged = [
      ...toArray(causeTypes),
      ...toArray(activeCauseTypes),
      ...toArray(defaultCauseTypes),
      ...toArray(teamCauseTypes),
    ];

    return Array.from(
      merged
        .filter((x) => x && x.id)
        .reduce((map, item) => {
          // team/last-in wins: spread over any previous
          map.set(item.id, { ...(map.get(item.id) || {}), ...item });
          return map;
        }, new Map())
        .values()
    );
  }, [causeTypes, activeCauseTypes, teamCauseTypes, defaultCauseTypes]);

  // All cause types including archived ones for existing saved causes
  const allCauseTypes = useMemo(() => {
    // Include all possible sources of cause types to ensure we don't lose archived/deleted ones
    const all = [
      ...toArray(causeTypes),
      ...toArray(activeCauseTypes),
      ...toArray(activeTeamCauseTypes),
      ...toArray(defaultCauseTypes),
      ...toArray(allTeamCauseTypeTranslations),
      ...toArray(teamCauseTypes),
    ];
    return all.filter(
      (item, index, self) =>
        item && item.id && index === self.findIndex((t) => t?.id === item.id)
    );
  }, [
    causeTypes,
    activeCauseTypes,
    defaultCauseTypes,
    activeTeamCauseTypes,
    allTeamCauseTypeTranslations,
    teamCauseTypes,
  ]);

  // Create lookup maps
  const causeTypesById = useMemo(
    () => Object.fromEntries(mergedCauseTypes.map((c) => [c.id, c])),
    [mergedCauseTypes]
  );

  const allCauseTypesById = useMemo(
    () => Object.fromEntries(allCauseTypes.map((c) => [c.id, c])),
    [allCauseTypes]
  );

  const translationsById = useMemo(
    () =>
      Object.fromEntries(toArray(causeTypesTranslations).map((t) => [t.id, t])),
    [causeTypesTranslations]
  );

  // Process team cause types
  const processTeamCauseTypes = async () => {
    const teamIds = Array.isArray(teamCauseTypes)
      ? (teamCauseTypes[0]?.causeTypes ?? [])
      : (teamCauseTypes?.causeTypes ?? []);
    const uniqueTeamIds = Array.isArray(teamIds) ? [...new Set(teamIds)] : [];

    return uniqueTeamIds.length ? await getCauseTypesByIds(uniqueTeamIds) : [];
  };

  // Get causes for a specific mood entry
  const getCausesForMood = (moodId) => {
    return (causes || []).filter(
      (m) => m.uid === userId && m.moodId === moodId
    );
  };

  // Enrich causes with translation data
  const enrichCausesWithTranslations = (
    causesToEnrich,
    useAllCauseTypes = false
  ) => {
    const primaryLookupMap = useAllCauseTypes
      ? allCauseTypesById
      : causeTypesById;
    const fallbackLookupMap = useAllCauseTypes
      ? causeTypesById
      : allCauseTypesById;

    return causesToEnrich.map((spec) => {
      // Try primary lookup first, then fallback
      let ct =
        primaryLookupMap[spec.causeTypeId] ||
        fallbackLookupMap[spec.causeTypeId];

      // If cause type is not found anywhere, preserve existing data
      if (!ct) {
        console.warn(
          `Cause type ${spec.causeTypeId} not found in any lookup map, preserving existing data`
        );

        // If the spec already has translation data, preserve it completely
        if (spec.causeTypeTranslation || spec.causeTitle) {
          return spec;
        }

        // Create a minimal fallback to avoid null errors
        return {
          ...spec,
          causeType: spec.causeType || null,
          causeTypeTranslation: spec.causeTypeTranslation || {
            ru: null,
            en: null,
            et: null,
          },
          causeTitle: spec.causeTitle || "Unknown cause",
        };
      }

      // Pick translation doc by its ID
      const tDoc = ct?.causeTranslationId
        ? translationsById[ct.causeTranslationId]
        : undefined;

      // Build tri-lingual triplet using helper (falls back to inline or mood)
      const ctt = toLangTriplet(tDoc, ct?.causeTypeTranslation, ct?.mood);

      const currentLang = i18n?.language || "en";
      const causeTitle =
        (ctt && (ctt[currentLang] ?? ctt.ru ?? ctt.en ?? ctt.et)) ??
        ct?.translation ??
        ct?.mood ??
        "Unknown cause";

      return {
        ...spec,
        causeType: ct,
        causeTypeTranslation: ctt,
        causeTitle,
      };
    });
  };

  return {
    mergedCauseTypes,
    allCauseTypes,
    causeTypesById,
    allCauseTypesById,
    translationsById,
    processTeamCauseTypes,
    getCausesForMood,
    enrichCausesWithTranslations,
    toLangTriplet,
  };
};

export default useCauseProcessor;
